/**
 * The "live" row of a PO line card in the Purchase Order Details Lines panel:
 * the open change request, the shipment in progress, and NCRs / rejects.
 *
 * Customer review 11 Sep (item 26e/f/g) — "each of the above to click
 * through to the relevant detail page". Each cell opens the PO line (in a new
 * tab, like the part-number link above it) on the tab that holds the detail,
 * or the ASN itself. A change request only counts when it is actionable
 * (canActOnProposal) — the same rule the line modal uses (review item 28).
 */
import type { ReactNode } from 'react';
import { FileWarning, Truck, Edit } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { PurchaseOrderLine, POInsightsLine } from '../../lib/api';
import { canActOnProposal } from './pendingProposal';

interface PODetailLineLiveRowProps {
  line: PurchaseOrderLine;
  /** Undefined while the order's insights load or when they failed. */
  insight?: POInsightsLine;
  insightsState: 'loading' | 'ready' | 'error';
  formatDate: (value: string) => string;
  formatQuantity: (value: number | string | null | undefined) => string;
}

const ASN_LIVE_LABELS: Record<string, string> = {
  created: 'Created',
  submitted: 'Submitted',
  in_transit: 'In Transit',
};

/** Deep link that opens this PO line's modal on one of its tabs. */
export const lineTabHref = (lineId: number, tab: string) =>
  `/purchasing?tab=po_lines&line=${lineId}&lineTab=${tab}`;

function LiveCell({ icon: Icon, label, href, children }: {
  icon: LucideIcon; label: string; href?: string; children: ReactNode;
}) {
  const body = <span className={href ? 'text-primary hover:underline' : 'text-muted-foreground'}>{children}</span>;
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
        <Icon className="h-3 w-3" /> {label}
      </label>
      <div className="font-medium mt-1 text-sm">
        {href ? <a href={href} target="_blank" rel="noopener noreferrer">{body}</a> : body}
      </div>
    </div>
  );
}

export default function PODetailLineLiveRow({ line, insight, insightsState, formatDate, formatQuantity }: PODetailLineLiveRowProps) {
  const pendingText = insightsState === 'loading' ? 'Loading…' : insightsState === 'error' ? 'Unavailable' : 'None';

  const changeParts: string[] = [];
  if (canActOnProposal(line)) {
    const dateLabel = line.acknowledge_status === 'change_proposed_customer' ? 'Required date' : 'Promise date';
    if (line.supplier_proposed_date) changeParts.push(`${dateLabel} → ${formatDate(line.supplier_proposed_date)}`);
    if (line.supplier_proposed_qty) changeParts.push(`Qty → ${formatQuantity(line.supplier_proposed_qty)}`);
    if (line.supplier_proposed_unit_price) changeParts.push(`Price → ${line.supplier_proposed_unit_price}`);
  }
  const asn = insight?.live_asns[0];
  const moreAsns = (insight?.live_asns.length ?? 0) - 1;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
      <LiveCell icon={Edit} label="Live Change Request" href={changeParts.length ? lineTabHref(line.id, 'change-requests') : undefined}>
        {changeParts.length
          ? `${changeParts.join(' · ')} (waiting on ${line.waiting_on || 'decision'})`
          : 'None'}
      </LiveCell>
      <LiveCell icon={Truck} label="Live ASN Shipment" href={asn ? `/purchasing?tab=asn&asn=${asn.id}` : undefined}>
        {asn
          ? `${asn.asn_number} · ${ASN_LIVE_LABELS[asn.status] || asn.status}${moreAsns > 0 ? ` (+${moreAsns} more)` : ''}`
          : insight ? 'None' : pendingText}
      </LiveCell>
      <LiveCell icon={FileWarning} label="NCR / Reject Qty" href={insight?.ncr_count ? lineTabHref(line.id, 'ncr') : undefined}>
        {insight
          ? insight.ncr_count
            ? `${insight.ncr_count} NCR${insight.ncr_count === 1 ? '' : 's'} · ${formatQuantity(insight.ncr_reject_qty)} rejected`
            : 'None'
          : pendingText}
      </LiveCell>
    </div>
  );
}
